// apps/client/src/hooks/useAllBrokersOverview.ts
// Fetches all-brokers commission summary for admin overview.

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { normaliseNetShare } from './normaliseNetShare';

export type OverviewPeriod = 'current_month' | 'last_3_months' | 'ytd' | 'last_12_months';


export interface BrokerOverviewEntry {
  brokerDocId: string;
  brokerName: string;
  officeId: string | null;
  officeName: string | null;
  baseSalaryCents: number | null;
  /** Point-in-time salary periods, oldest first. ISO dates from the callable. */
  salaryHistory?: { effectiveFrom: string; effectiveTo: string | null; baseSalaryCents: number }[];
  brokerIntroductionPct: number | null;
  companyIntroductionPct: number | null;
  /** PR C — 3-tier extensions. Null on legacy / 2-tier brokers. */
  trailBrokerIntroductionPct?: number | null;
  trailCompanyIntroductionPct?: number | null;
  upfrontSelfSourcedPct?: number | null;
  trailSelfSourcedPct?: number | null;
  /** Per-broker activation flag — '2-tier' | '3-tier'. Default '2-tier'. */
  commissionStructure?: '2-tier' | '3-tier' | null;
  /** Per-broker trail visibility mode — 'all' | 'self_sourced_only'. */
  trailVisibilityMode?: 'all' | 'self_sourced_only' | null;
  totalCommsCents: number;
  /** Leg- and tier-aware modelled share, computed server-side (2026-08-12).
   *  Replaces the page's old "multiply the gross by one default percentage"
   *  estimate, which could not express a per-loan tier, a trail override, a
   *  locked pct, arrears/discharged scoring zero, or the visibility rules. */
  totalBrokerShareCents: number;
  monthlyBrokerShare: { month: string; cents: number }[];
  /** What the broker takes home after recipient allocations. Falls back to the
   *  share figures for a cached client served by an older callable, so the
   *  reconciliation never renders blank. */
  totalBrokerNetCents: number;
  monthlyBrokerNet: { month: string; cents: number }[];
  totalRecipientAllocatedCents: number;
  /** Active manual adjustments already included in the gross and share totals. */
  manualAdjustmentCount: number;
  trailStartMonth?: string | null;
  totalPaidCents: number;
  amtOnTopOfBaseCents: number;
  carryOverCents: number;
  visiblePaymentTypes: string[] | null;
  defaultIntroductionType: string | null;
  hasConfig: boolean;
  untaggedLoanCount: number;
  taggedLoanCount: number;
  totalLoanCount: number;
  monthlyComms: { month: string; cents: number }[];
  templateIds: string[];
}

/** PR 1A-v3 (2026-05-13) cross-broker rollups returned by
 *  getAllBrokersCommissionOverview. Surfaced in the overview UI below the
 *  broker table so admins see at a glance which assistants/analysts are
 *  funded across multiple brokers. */
export interface AssistantRollupEntry {
  assistantId: string;
  displayName: string;
  brokers: Array<{ brokerDocId: string; brokerName: string; sharePct: number }>;
  totalSharePct: number;
}
export interface AnalystRollupEntry {
  analystId: string;
  displayName: string;
  hasLogin: boolean;
  brokers: Array<{
    brokerDocId: string;
    brokerName: string;
    upfrontSharePct: number;
    trailSharePct: number;
  }>;
  totalUpfrontSharePct: number;
  totalTrailSharePct: number;
  /** PR 1A-v4 (2026-05-14): performance targets bound to this analyst across
   *  all funding brokers. Empty when the analyst has no active targets.
   *  PR 1A-v4.1 (2026-05-14): progress fields populated by the backend's
   *  computeAnalystTargetProgress helper. Falls back to 0 if BQ failed. */
  activeTargets: Array<{
    targetId: string;
    brokerDocId: string;
    brokerName: string;
    label: string;
    type: string;
    period: string;
    thresholdCents: number;
    bonusCents: number;
    commissionComponents: string[] | null;
    currentValueCents: number;
    percentComplete: number;
  }>;
}

interface UseAllBrokersOverviewReturn {
  brokers: BrokerOverviewEntry[];
  assistantRollup: AssistantRollupEntry[];
  analystRollup: AnalystRollupEntry[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Calls `getAllBrokersCommissionOverview` callable function.
 * Admin-only: returns all brokers with their commission settings and pay data.
 */
export function useAllBrokersOverview(
  companyId: string | undefined,
  period: OverviewPeriod = 'current_month',
  entityCode?: string
): UseAllBrokersOverviewReturn {
  const [brokers, setBrokers] = useState<BrokerOverviewEntry[]>([]);
  const [assistantRollup, setAssistantRollup] = useState<AssistantRollupEntry[]>([]);
  const [analystRollup, setAnalystRollup] = useState<AnalystRollupEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const getAllBrokersOverview = httpsCallable(
        functions,
        'getAllBrokersCommissionOverview'
      );

      const result = await getAllBrokersOverview({
        companyId,
        period,
        entityCode,
      });

      const data = result.data as {
        brokers: any[];
        assistantRollup?: AssistantRollupEntry[];
        analystRollup?: AnalystRollupEntry[];
      };

      setBrokers((data.brokers || []).map((b: any) => ({
        ...b,
        totalCommsCents: b.totalCommsCents ?? 0,
        totalBrokerShareCents: b.totalBrokerShareCents ?? 0,
        monthlyBrokerShare: Array.isArray(b.monthlyBrokerShare) ? b.monthlyBrokerShare : [],
        ...normaliseNetShare(b),
        manualAdjustmentCount: b.manualAdjustmentCount ?? 0,
        totalPaidCents: b.totalPaidCents ?? 0,
        amtOnTopOfBaseCents: b.amtOnTopOfBaseCents ?? 0,
        carryOverCents: b.carryOverCents ?? 0,
        untaggedLoanCount: b.untaggedLoanCount ?? 0,
        taggedLoanCount: b.taggedLoanCount ?? 0,
        totalLoanCount: b.totalLoanCount ?? 0,
        monthlyComms: Array.isArray(b.monthlyComms) ? b.monthlyComms : [],
        defaultIntroductionType: b.defaultIntroductionType ?? null,
        hasConfig: b.hasConfig ?? false,
        templateIds: Array.isArray(b.templateIds) ? b.templateIds : [],
      })));
      setAssistantRollup(Array.isArray(data.assistantRollup) ? data.assistantRollup : []);
      setAnalystRollup(Array.isArray(data.analystRollup) ? data.analystRollup : []);
    } catch (err: any) {
      console.error('[useAllBrokersOverview] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch brokers overview');
      setBrokers([]);
      setAssistantRollup([]);
      setAnalystRollup([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, period, entityCode]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return { brokers, assistantRollup, analystRollup, loading, error, refetch: fetchOverview };
}
